/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useMemo, useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useDispatch} from 'react-redux'
import {login, resend2FA, verify2FA} from '../../services'
import {loginUser} from '../../../../redux/states/user'
import {AxiosInterceptor} from '../../../../interceptors'
import {ShowSnackBar} from '../../../../util/functions'
import {UserModel} from '../../models'

const loginSchema = Yup.object().shape({
  email: Yup.string().required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function LoginForm() {
  const [loading, setLoading] = useState(false)
  const [pendingUser, setPendingUser] = useState<UserModel | null>(null)
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', ''])
  const [otpLoading, setOtpLoading] = useState(false)
  const [is2FAStep, setIs2FAStep] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const [resendingOtp, setResendingOtp] = useState(false)

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const dispatch = useDispatch()

  const otpCode = useMemo(() => otpDigits.join(''), [otpDigits])

  const finalizeLogin = (user: UserModel) => {
    if (!user.cambioContrasena) {
      dispatch(loginUser(user))
    }
    AxiosInterceptor(user)
  }

  const handleOtpChange = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, '').slice(-1)
    const next = [...otpDigits]
    next[index] = value
    setOtpDigits(next)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleVerify2FA = async () => {
    if (!pendingUser) {
      ShowSnackBar('No existe usuario pendiente por validar', 'error')
      return
    }

    if (otpCode.length !== 6) {
      ShowSnackBar('Debes ingresar los 6 dígitos', 'error')
      return
    }

    try {
      setOtpLoading(true)

      const {
        data: {error, message, data},
      } = await verify2FA({
        email: pendingUser.email,
        token: otpCode,
      })
      if (error) {
        ShowSnackBar(message || 'Código de verificación inválido', 'error')
        setOtpLoading(false)
        return
      } else {
        if (data.valido) {
          ShowSnackBar('Código verificado exitosamente', 'success')
          finalizeLogin({...pendingUser, token: data?.token ?? ''})
        } else {
          ShowSnackBar('Código de verificación inválido', 'error')
        }
      }
    } catch (error) {
      console.error(error)
      ShowSnackBar('No se pudo validar el código de verificación', 'error')
    } finally {
      setOtpLoading(false)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {email, password} = values
        const {
          data: {data: user, error, message},
        } = await login(email, password)

        if (!error) {
          if (user.validarGates && user.entorno === 'ONLINE' && user.esUsuarioSodexo) {
            window.location.href =
              process.env.REACT_APP_DEV_API_BASE + '/gates/backend/api/gates/callgates'
            setLoading(false)
            setStatus('')
            setSubmitting(false)
            return
          }

          if (user.validar2Factor) {
            setPendingUser(user)
            setIs2FAStep(true)
            setOtpDigits(['', '', '', '', '', ''])
            setLoading(false)
            setSubmitting(false)
            setCountdown(60)
            return
          }

          finalizeLogin(user)
          setLoading(false)
          setSubmitting(false)
        } else {
          setStatus(message)
          setSubmitting(false)
          setLoading(false)
        }
      } catch (error) {
        console.error(error)
        setStatus('Las credenciales son incorrectas')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleResendOtp = async () => {
    if (!pendingUser) {
      ShowSnackBar('No existe usuario pendiente por validar', 'error')
      return
    }

    try {
      setResendingOtp(true)

      const responseResend = await resend2FA({email: pendingUser?.email})
      if (responseResend?.data?.error) {
        ShowSnackBar(responseResend.data.message ?? 'Hubo un error al enviar codigo', 'error')
      } else {
        setOtpDigits(['', '', '', '', '', ''])
        setCountdown(60)
        ShowSnackBar('Se reenvió el código exitosamente', 'success')
      }
    } catch (error) {
      console.error(error)
      ShowSnackBar('No se pudo reenviar el código', 'error')
    } finally {
      setResendingOtp(false)
    }
  }

  useEffect(() => {
    if (!is2FAStep) return
    if (countdown <= 0) return
    const intervalId = window.setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [is2FAStep, countdown])

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {!is2FAStep ? (
        <>
          <div className='text-center mb-11'>
            <h1 className='text-dark fw-bolder mb-3'>Iniciar sesión</h1>
          </div>

          {formik.status ? (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          ) : null}

          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark'>Usuario</label>
            <input
              placeholder='Ingresa tu correo'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                },
              )}
              type='email'
              name='email'
              autoComplete='off'
            />
            {formik.touched.email && formik.errors.email && (
              <div className='fv-plugins-message-container'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            )}
          </div>

          <div className='fv-row mb-3'>
            <div className='d-flex flex-stack mb-2'>
              <label className='form-label fw-bold text-dark fs-6 mb-0'> Contraseña </label>
            </div>

            <input
              type='password'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                },
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>

          <div className='d-grid mb-10'>
            <button
              type='submit'
              id='kt_sign_in_submit'
              className='btn btn-primary'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>Ingresar</span>}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className='text-center mb-10'>
            <h1 className='text-dark fw-bolder mb-3'>Verifica tu email</h1>
            <div className='text-muted fw-semibold fs-6'>
              Ingresa el código que se envio a tu email
            </div>
            <div className='fw-bold mt-2'>{pendingUser?.email ?? ''}</div>
          </div>

          <div className='d-flex justify-content-center gap-2 mb-6'>
            {otpDigits.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(idx, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                className='form-control text-center'
                style={{width: 44, height: 44}}
                inputMode='numeric'
                maxLength={1}
              />
            ))}
          </div>

          <div className='text-center text-muted mb-8'>
            {countdown > 0 ? (
              <>No recibiste el código? ({countdown}s)</>
            ) : (
              <>
                No recibiste el código?
                <button
                  type='button'
                  className='btn btn-link p-0 align-baseline ms-2'
                  onClick={handleResendOtp}
                  disabled={resendingOtp}
                >
                  {resendingOtp ? 'Reenviando...' : 'Re enviar'}
                </button>
              </>
            )}
          </div>

          <div className='d-grid mb-10'>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleVerify2FA}
              disabled={otpLoading}
            >
              {!otpLoading && <span className='indicator-label'>Continuar</span>}
              {otpLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Validando...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </>
      )}
    </form>
  )
}
